import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { UpdateAvatarDialogComponent, UpdateAvatarDialogData } from './update-avatar-dialog/update-avatar-dialog.component';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
        TranslocoModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _dialog = inject(MatDialog);
    private _authService = inject(AuthService);
    private _snackBar = inject(MatSnackBar);
    private _translocoService = inject(TranslocoService);

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Open the update avatar dialog and upload the selected avatar
     */
    updateAvatar(): void {
        this._dialog
            .open<UpdateAvatarDialogComponent, UpdateAvatarDialogData>(UpdateAvatarDialogComponent, {
                panelClass: 'update-avatar-dialog-panel',
                data: { currentAvatar: this.user?.avatar },
            })
            .afterClosed()
            .subscribe((file: File | undefined) => {
                if (!file) {
                    return;
                }

                this._userService.updateAvatar(file).subscribe({
                    next: () => {
                        // Refresh the current user via the existing getMe API
                        this._authService.signInUsingToken().subscribe();

                        this._snackBar.open(
                            this._translocoService.translate('user.update-avatar-success'),
                            this._translocoService.translate('button.close'),
                            { duration: 5000 }
                        );
                    },
                    error: () => {
                        this._snackBar.open(
                            this._translocoService.translate('user.update-avatar-failed'),
                            this._translocoService.translate('button.close'),
                            { duration: 5000 }
                        );
                    },
                });
            });
    }
}
